import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import { Checkbox, Divider } from 'antd';
import { TbExclamationCircle } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { DragDropContext, Draggable, DragHandle, Droppable, type DropResult } from '@/shared/components/drag-and-drop';
import CompactCollapsible from '@/shared/components/layouts/sections/CompactCollapsible';
import EmptyView from '@/shared/components/layouts/views/EmptySection';
import ListItemSkeleton from '@/shared/components/skeletons/ListItemSkeleton';
import MiddleDot from '@/shared/components/typography/MiddleDot';
import { getWorkspaceConceptIconType, WorkspaceResourceGlyph } from '@/shared/components/workspace-resources';

import type { LifecycleTaskCategory } from '../domain';
import { reorderLifecycleHierarchy } from '../hierarchyReorder';
import { lifecycleCategorySelectionKey, lifecycleTaskGroupSelectionKey } from '../hierarchySelection';
import LifecycleStageCollapsible from '../LifecycleStageCollapsible';

export type LifecycleHierarchyState = 'empty' | 'error' | 'loading' | 'ready';

interface LifecycleHierarchyViewProps {
    categoryActions?: (
        category: LifecycleTaskCategory,
        controls: { open: boolean; setOpen: (open: boolean) => void },
    ) => ReactNode;
    categories: LifecycleTaskCategory[];
    dragDisabled?: boolean;
    emptyDescription?: string;
    emptyLabel?: string;
    state?: LifecycleHierarchyState;
    onCategoriesChange?: (categories: LifecycleTaskCategory[]) => void;
    onSelectedKeysChange?: (keys: ReadonlySet<string>) => void;
    selectedKeys?: ReadonlySet<string>;
    taskGroupActions?: (
        group: LifecycleTaskCategory['taskGroups'][number],
        controls: { open: boolean; setOpen: (open: boolean) => void },
    ) => ReactNode;
}

const CATEGORY_DROPPABLE_ID = 'lifecycle-categories';
const GROUP_DROPPABLE_PREFIX = 'lifecycle-groups:';
const LifecycleCategoryIcon = getWorkspaceConceptIconType('lifecycleCategory');
const LifecycleTaskGroupIcon = getWorkspaceConceptIconType('lifecycleTaskGroup');

const categoryIdFromGroupDroppable = (droppableId: string) =>
    droppableId.startsWith(GROUP_DROPPABLE_PREFIX) ? droppableId.slice(GROUP_DROPPABLE_PREFIX.length) : undefined;

const LifecycleHierarchyView: FC<LifecycleHierarchyViewProps> = ({
    categories,
    categoryActions,
    dragDisabled = false,
    emptyDescription = 'Add a task category and group to begin structuring this initiative.',
    emptyLabel = 'No Lifecycle Tasks',
    state = 'ready',
    onCategoriesChange,
    onSelectedKeysChange,
    selectedKeys = new Set<string>(),
    taskGroupActions,
}) => {
    const { token } = useAntDesignTheme();
    const [openCategoryIds, setOpenCategoryIds] = useState<ReadonlySet<string>>(
        () => new Set(categories.filter((category) => category.defaultOpen ?? true).map((category) => category.id)),
    );
    const reorderEnabled = Boolean(onCategoriesChange) && !dragDisabled;

    const setItemSelected = (key: string, selected: boolean) => {
        if (!onSelectedKeysChange) {
            return;
        }

        const nextKeys = new Set(selectedKeys);

        if (selected) {
            nextKeys.add(key);
        } else {
            nextKeys.delete(key);
        }

        onSelectedKeysChange(nextKeys);
    };

    const onDragEnd = (result: DropResult) => {
        if (!result.destination || !reorderEnabled) {
            return;
        }

        if (
            result.source.droppableId === result.destination.droppableId &&
            result.source.index === result.destination.index
        ) {
            return;
        }

        if (result.type !== 'LIFECYCLE_CATEGORY' && result.type !== 'LIFECYCLE_TASK_GROUP') {
            return;
        }

        const nextCategories = reorderLifecycleHierarchy({
            categories,
            destination: {
                categoryId: categoryIdFromGroupDroppable(result.destination.droppableId),
                index: result.destination.index,
            },
            source: {
                categoryId: categoryIdFromGroupDroppable(result.source.droppableId),
                index: result.source.index,
            },
            type: result.type,
        });

        if (nextCategories) {
            onCategoriesChange?.(nextCategories);
        }
    };

    if (state === 'loading') {
        return (
            <div aria-label='Loading lifecycle hierarchy' style={{ display: 'grid', gap: '0.75rem', padding: '1rem' }}>
                {Array.from({ length: 3 }, (_, index) => (
                    <div
                        key={index}
                        style={{
                            border: `1px solid ${token.colorBorder}`,
                            borderRadius: token.borderRadius,
                            padding: '0.75rem',
                        }}
                    >
                        <ListItemSkeleton avatar avatarShape='square' lineWidths={['38%', '64%']} />
                    </div>
                ))}
            </div>
        );
    }

    if (state === 'error') {
        return (
            <EmptyView
                description='Refresh the view to try loading this lifecycle again.'
                height='18rem'
                icon={<TbExclamationCircle size='1.25rem' />}
                label='Unable to Load Lifecycle'
            />
        );
    }

    if (state === 'empty' || categories.length === 0) {
        return (
            <EmptyView
                description={emptyDescription}
                height='18rem'
                icon={<WorkspaceResourceGlyph resource='task' size='1.25rem' />}
                label={emptyLabel}
            />
        );
    }

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable
                decoratePlaceholder={false}
                direction='vertical'
                droppableId={CATEGORY_DROPPABLE_ID}
                type='LIFECYCLE_CATEGORY'
            >
                {(categoryDroppableProvided) => (
                    <div
                        ref={categoryDroppableProvided.innerRef}
                        {...categoryDroppableProvided.droppableProps}
                        style={{ display: 'grid', gap: '0.75rem', padding: '1rem' }}
                    >
                        {categories.map((category, categoryIndex) => {
                            const open = openCategoryIds.has(category.id);
                            const taskCount = category.taskGroups.reduce(
                                (total, group) => total + group.tasks.length,
                                0,
                            );
                            const setCategoryOpen = (nextOpen: boolean) => {
                                const nextOpenCategoryIds = new Set(openCategoryIds);

                                if (nextOpen) {
                                    nextOpenCategoryIds.add(category.id);
                                } else {
                                    nextOpenCategoryIds.delete(category.id);
                                }

                                setOpenCategoryIds(nextOpenCategoryIds);
                            };

                            const categorySelectionKey = lifecycleCategorySelectionKey(category.id);

                            return (
                                <Draggable
                                    draggableId={categorySelectionKey}
                                    index={categoryIndex}
                                    isDragDisabled={!reorderEnabled}
                                    key={category.id}
                                >
                                    {(categoryDraggableProvided) => (
                                        <div
                                            ref={categoryDraggableProvided.innerRef}
                                            {...categoryDraggableProvided.draggableProps}
                                            style={categoryDraggableProvided.draggableProps.style}
                                        >
                                            <CompactCollapsible
                                                bordered
                                                id={`lifecycle-category-${category.id}`}
                                                description={category.description}
                                                headerPrefix={
                                                    <DragHandle
                                                        disabled={!reorderEnabled}
                                                        dragHandleProps={categoryDraggableProvided.dragHandleProps}
                                                        label={`Reorder ${category.label} Task Category`}
                                                        style={{ height: '1.5rem', minWidth: '1.5rem' }}
                                                    />
                                                }
                                                headerPrefixHoverOnly
                                                headerSelection={
                                                    onSelectedKeysChange ? (
                                                        <Checkbox
                                                            aria-label={`Select ${category.label} Task Category`}
                                                            checked={selectedKeys.has(categorySelectionKey)}
                                                            onChange={(event) =>
                                                                setItemSelected(
                                                                    categorySelectionKey,
                                                                    event.target.checked,
                                                                )
                                                            }
                                                        />
                                                    ) : null
                                                }
                                                headerSelectionActive={selectedKeys.has(categorySelectionKey)}
                                                icon={
                                                    <LifecycleCategoryIcon
                                                        style={{ color: category.color ?? token.colorPrimary }}
                                                    />
                                                }
                                                label={category.label}
                                                open={open}
                                                headerExtra={
                                                    <span
                                                        style={{
                                                            alignItems: 'center',
                                                            display: 'inline-flex',
                                                            gap: '0.75rem',
                                                        }}
                                                    >
                                                        <small style={{ color: token.colorTextTertiary }}>
                                                            {category.taskGroups.length}{' '}
                                                            {category.taskGroups.length === 1 ? 'Group' : 'Groups'} ·{' '}
                                                            {taskCount} {taskCount === 1 ? 'Task' : 'Tasks'}
                                                        </small>
                                                        {categoryActions?.(category, {
                                                            open,
                                                            setOpen: setCategoryOpen,
                                                        })}
                                                    </span>
                                                }
                                                contentStyle={{ padding: 0 }}
                                                descriptionStyle={{
                                                    marginBottom: '0.75rem',
                                                    padding: '0.5rem 0.75rem 0',
                                                }}
                                                headerStyle={{ paddingBlock: '0.25rem' }}
                                                onOpenChange={setCategoryOpen}
                                            >
                                                <Droppable
                                                    decorateActive
                                                    decoratePlaceholder={false}
                                                    direction='vertical'
                                                    droppableId={`${GROUP_DROPPABLE_PREFIX}${category.id}`}
                                                    type='LIFECYCLE_TASK_GROUP'
                                                >
                                                    {(groupDroppableProvided) => (
                                                        <div
                                                            ref={groupDroppableProvided.innerRef}
                                                            {...groupDroppableProvided.droppableProps}
                                                            style={{
                                                                display: 'grid',
                                                                gap: 0,
                                                                minHeight: '3rem',
                                                            }}
                                                        >
                                                            {category.taskGroups.length ? (
                                                                category.taskGroups.map((group, groupIndex) => {
                                                                    const groupSelectionKey =
                                                                        lifecycleTaskGroupSelectionKey(group.id);

                                                                    return (
                                                                        <Draggable
                                                                            draggableId={groupSelectionKey}
                                                                            index={groupIndex}
                                                                            isDragDisabled={!reorderEnabled}
                                                                            key={group.id}
                                                                        >
                                                                            {(groupDraggableProvided) => (
                                                                                <div
                                                                                    ref={
                                                                                        groupDraggableProvided.innerRef
                                                                                    }
                                                                                    {...groupDraggableProvided.draggableProps}
                                                                                    style={
                                                                                        groupDraggableProvided
                                                                                            .draggableProps.style
                                                                                    }
                                                                                >
                                                                                    <LifecycleStageCollapsible
                                                                                        actions={(controls) =>
                                                                                            taskGroupActions?.(
                                                                                                group,
                                                                                                controls,
                                                                                            )
                                                                                        }
                                                                                        dragDisabled={!reorderEnabled}
                                                                                        dragHandleProps={
                                                                                            groupDraggableProvided.dragHandleProps
                                                                                        }
                                                                                        edgeToEdge
                                                                                        selected={selectedKeys.has(
                                                                                            groupSelectionKey,
                                                                                        )}
                                                                                        stage={group}
                                                                                        onSelectedChange={(selected) =>
                                                                                            setItemSelected(
                                                                                                groupSelectionKey,
                                                                                                selected,
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                        </Draggable>
                                                                    );
                                                                })
                                                            ) : (
                                                                <EmptyView
                                                                    description='Add a task group to organize the tasks in this category.'
                                                                    height='8rem'
                                                                    icon={<LifecycleTaskGroupIcon size='1rem' />}
                                                                    label='No Task Groups'
                                                                />
                                                            )}
                                                            {groupDroppableProvided.placeholder}
                                                            {category.taskGroups.length ? (
                                                                <Divider plain style={{ margin: '0.75rem 0' }}>
                                                                    <small
                                                                        style={{
                                                                            color: token.colorTextTertiary,
                                                                            fontWeight: 400,
                                                                        }}
                                                                    >
                                                                        {category.taskGroups.length}{' '}
                                                                        {category.taskGroups.length === 1
                                                                            ? 'Group'
                                                                            : 'Groups'}
                                                                        <MiddleDot horizontalMargin='0.375rem' />
                                                                        {taskCount} {taskCount === 1 ? 'Task' : 'Tasks'}
                                                                    </small>
                                                                </Divider>
                                                            ) : null}
                                                        </div>
                                                    )}
                                                </Droppable>
                                            </CompactCollapsible>
                                        </div>
                                    )}
                                </Draggable>
                            );
                        })}
                        {categoryDroppableProvided.placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
};

export default LifecycleHierarchyView;
